/**
 * O período do recorte, resolvido do mesmo jeito nos dois lados.
 *
 * O flat-pickr devolve uma faixa como "2026-08-01 até 2026-08-31", ou nada
 * quando o usuário não escolheu período.
 *
 * POR QUE "NADA" NÃO PODE CONTINUAR SENDO NADA
 *
 * A rota da tabela aceita período vazio e resolve para HOJE..HOJE
 * (`KpiTableReadService::resolveDateWindow`). A rota de detalhamento EXIGE as
 * duas datas. Deixar o vazio passar adiante fazia todo clique numa célula
 * responder 422 até o usuário escolher um período — e o erro chegava como
 * "não foi possível carregar o detalhamento", que não ajuda ninguém.
 *
 * Resolver o padrão aqui faz as duas chamadas olharem a mesma janela por
 * construção. Se o backend mudar o padrão dele, este arquivo é o lugar de
 * mudar junto, e o teste abaixo é o que avisa que existe um pacto.
 */

const doisDigitos = numero => String(numero).padStart(2, '0')

const emYmd = data => `${data.getFullYear()}-${doisDigitos(data.getMonth() + 1)}-${doisDigitos(data.getDate())}`

/**
 * @param {string|undefined} rangeDate faixa do flat-pickr
 * @param {Date} hoje injetável para o teste não depender do calendário
 * @returns {{startDate: string, endDate: string}}
 */
const resolvePeriod = (rangeDate, hoje = new Date()) => {
  let inicio
  let fim

  if (rangeDate) {
    [inicio, fim] = String(rangeDate).split(' até ')
  }

  // Uma data só acontece enquanto o usuário ainda está escolhendo a faixa: o
  // flat-pickr emite o primeiro clique antes do segundo.
  inicio = (inicio || '').trim() || (fim || '').trim()
  fim = (fim || '').trim() || inicio

  if (!inicio) {
    inicio = emYmd(hoje)
    fim = inicio
  }

  return { startDate: inicio, endDate: fim }
}

/** Competência aberta: o fim do recorte é hoje ou depois. */
const isOpenPeriod = (endDate, hoje = new Date()) => {
  if (!endDate) {
    return true
  }
  const referencia = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate())
  const [ano, mes, dia] = String(endDate).split('-').map(Number)

  if (!ano || !mes || !dia) {
    return true
  }

  return new Date(ano, mes - 1, dia) >= referencia
}

const paraData = ymd => {
  const [ano, mes, dia] = String(ymd).split('-').map(Number)
  return ano && mes && dia ? new Date(ano, mes - 1, dia) : null
}

const DIA_EM_MS = 24 * 60 * 60 * 1000

/** Dias no intervalo, inclusive nas duas pontas. */
const daysBetween = (inicio, fim) => {
  const a = paraData(inicio)
  const b = paraData(fim)
  if (!a || !b || b < a) {
    return 0
  }
  return Math.round((b - a) / DIA_EM_MS) + 1
}

/**
 * Quanto do período já é fotografia, e quanto ainda está vivo.
 *
 * O Faturômetro não é um relatório que se recalcula inteiro a cada abertura:
 * dias antigos foram materializados e estão congelados; os últimos continuam
 * sendo recomputados a cada leitura. A tela precisa dizer isso, senão dois
 * usuários comparam números tirados em momentos diferentes e um deles acha que
 * o sistema errou.
 *
 * `ranges` vem de GET /api/consultant_performance/snapshot_ranges, como
 * intervalos [{startDate, endDate}] dos dias prontos.
 */
const snapshotSummary = (inicio, fim, ranges) => {
  const total = daysBetween(inicio, fim)

  if (!total) {
    return {
      total: 0, consolidated: 0, live: 0, ratio: 0,
    }
  }

  // Os intervalos vêm do servidor e podem passar das bordas do recorte; contar
  // dia a dia dentro da janela evita somar dia que a tela nem está mostrando.
  const dentro = new Set()
  const bordaInicial = paraData(inicio)
  const bordaFinal = paraData(fim)

  const intervalos = ranges || []
  intervalos.forEach(intervalo => {
    let cursor = paraData(intervalo.startDate)
    const ate = paraData(intervalo.endDate)
    if (!cursor || !ate) {
      return
    }
    while (cursor <= ate) {
      if (cursor >= bordaInicial && cursor <= bordaFinal) {
        dentro.add(cursor.getTime())
      }
      cursor = new Date(cursor.getTime() + DIA_EM_MS)
    }
  })

  const consolidated = Math.min(dentro.size, total)

  return {
    total,
    consolidated,
    live: total - consolidated,
    ratio: consolidated / total,
  }
}

const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho',
  'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro']

const diaDeHoje = hoje => new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate())

/**
 * Os atalhos de período, na ordem em que aparecem no calendário.
 *
 * A tela é lida por competência, e quem abre o Faturômetro quase sempre quer
 * uma de quatro perguntas: como está o mês, como fechou o anterior, o
 * trimestre, o ano. Montar qualquer uma delas no calendário custava dois
 * cliques certeiros num mês que às vezes nem está na tela.
 *
 * As faixas abertas terminam HOJE, e não no fim do mês: o backend não tem o que
 * somar depois de hoje, e um "até 30/09" num dia 22 promete um número que ainda
 * não existe.
 */
const PRESETS = [
  { key: 'mes', label: 'Este mês' },
  { key: 'mesAnterior', label: 'Mês anterior' },
  { key: 'trimestre', label: 'Este trimestre' },
  { key: 'ano', label: 'Este ano' },
]

/** A faixa de um atalho, como {startDate, endDate} em Y-m-d. */
const presetRange = (key, hoje = new Date()) => {
  const dia = diaDeHoje(hoje)
  const ano = dia.getFullYear()
  const mes = dia.getMonth()

  if (key === 'mesAnterior') {
    return { startDate: emYmd(new Date(ano, mes - 1, 1)), endDate: emYmd(new Date(ano, mes, 0)) }
  }
  if (key === 'trimestre') {
    return { startDate: emYmd(new Date(ano, mes - (mes % 3), 1)), endDate: emYmd(dia) }
  }
  if (key === 'ano') {
    return { startDate: emYmd(new Date(ano, 0, 1)), endDate: emYmd(dia) }
  }
  return { startDate: emYmd(new Date(ano, mes, 1)), endDate: emYmd(dia) }
}

/**
 * A competência inteira `delta` meses antes ou depois da que contém `endDate`.
 *
 * Anda pelo FIM do recorte, e não pelo início: de "Este trimestre" (jul a hoje)
 * o ‹ vai para agosto, o mês anterior ao que se está vendo terminar — e não para
 * junho, que é o que andar pelo início daria.
 *
 * O mês corrente para em hoje, pela mesma razão dos atalhos. Depois de hoje não
 * há competência: devolve null, e o botão › fica desligado.
 */
const shiftMonth = (endDate, delta, hoje = new Date()) => {
  const dia = diaDeHoje(hoje)
  const base = paraData(endDate) || dia
  const inicio = new Date(base.getFullYear(), base.getMonth() + delta, 1)

  if (inicio > dia) {
    return null
  }

  const fim = new Date(inicio.getFullYear(), inicio.getMonth() + 1, 0)
  return { startDate: emYmd(inicio), endDate: emYmd(fim > dia ? dia : fim) }
}

/**
 * O nome curto do recorte, para a etiqueta ao lado do campo.
 *
 * Um atalho se chama pelo nome dele; uma competência inteira, pelo mês
 * ("Agosto 2026"); qualquer outra faixa é "Personalizado". O mês corrente até
 * hoje é "Este mês", e não "Setembro 2026": é o atalho, e a etiqueta precisa
 * acender o mesmo botão que o calendário acende.
 *
 * `preferido` é o atalho que a pessoa CLICOU. Ele desempata quando dois atalhos
 * dão a mesma faixa: no primeiro mês de um trimestre "Este trimestre" e "Este
 * mês" são o mesmo período (out/2026: 01/10 a hoje), e sem o desempate o clique
 * no trimestre virava a etiqueta "Este mês" e acendia o botão do mês.
 */
const presetDaFaixa = (startDate, endDate, hoje, preferido) => {
  const bate = key => {
    const faixa = presetRange(key, hoje)
    return faixa.startDate === startDate && faixa.endDate === endDate
  }
  if (preferido && PRESETS.some(item => item.key === preferido) && bate(preferido)) {
    return PRESETS.find(item => item.key === preferido)
  }
  return PRESETS.find(item => bate(item.key)) || null
}

const periodLabel = (startDate, endDate, hoje = new Date(), preferido = null) => {
  const preset = presetDaFaixa(startDate, endDate, hoje, preferido)
  if (preset) {
    return preset.label
  }

  const inicio = paraData(startDate)
  const fim = paraData(endDate)
  if (inicio && fim && inicio.getDate() === 1
    && inicio.getFullYear() === fim.getFullYear() && inicio.getMonth() === fim.getMonth()
    && fim.getDate() === new Date(fim.getFullYear(), fim.getMonth() + 1, 0).getDate()) {
    return `${MESES[inicio.getMonth()]} ${inicio.getFullYear()}`
  }

  return 'Personalizado'
}

/** A chave do atalho que descreve a faixa, ou null. Acende o botão certo. */
const presetKeyOf = (startDate, endDate, hoje = new Date(), preferido = null) => {
  const preset = presetDaFaixa(startDate, endDate, hoje, preferido)
  return preset ? preset.key : null
}

module.exports = {
  PRESETS,
  daysBetween,
  emYmd,
  isOpenPeriod,
  periodLabel,
  presetKeyOf,
  presetRange,
  resolvePeriod,
  shiftMonth,
  snapshotSummary,
}
